import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import i18n from 'modules/core/lib/i18n'

import { ProjectWorker } from 'modules/expenses/hooks/useProjects'
import { Expensable } from 'modules/expenses/types/Expensable'

export interface FormProject {
  projectWorkerId: string | null
  subprojectId: string | null
}

interface Props {
  value: FormProject
  defaultValue?: NonNullable<Expensable['projects']>[number]
  projectWorkers: ProjectWorker[]
  loading?: boolean
  readonly?: boolean
  onChange: (project: FormProject | null) => void
  mandatory?: boolean
  errorMessage?: string
}

const defaultToProjectWorker = (defaultValue: Props['defaultValue']): ProjectWorker | null => {
  if (!defaultValue?.projectWorker?.imputableProject) return null

  const { projectWorker, subproject } = defaultValue

  return {
    id: projectWorker.id,
    assigned: projectWorker.assigned,
    imputableProject: {
      ...projectWorker.imputableProject!,
      subprojects: subproject ? [subproject] : [],
    },
  }
}

const projectWorkersWithDefault = (
  projectWorkers: Props['projectWorkers'],
  defaultValue: Props['defaultValue']
) => {
  const defaultProjectWorker = defaultToProjectWorker(defaultValue)

  if (!defaultProjectWorker || projectWorkers.some(({ id }) => id === defaultProjectWorker.id)) {
    return projectWorkers
  }

  return [...projectWorkers, defaultProjectWorker]
}

const ProjectFields = ({
  value,
  defaultValue,
  readonly,
  loading,
  projectWorkers: originalProjectWorkers,
  onChange,
  mandatory = false,
  errorMessage,
}: Props) => {
  const projectWorkers = projectWorkersWithDefault(originalProjectWorkers, defaultValue)

  const project = projectWorkers.find(({ id }) => id === value?.projectWorkerId)?.imputableProject

  const subprojects = project?.subprojects ?? []
  const subproject = subprojects.find(({ id }) => id === value?.subprojectId)

  const showErrorMessage = (name?: string | null) => {
    if (errorMessage && !name) return errorMessage
  }

  if (!projectWorkers.length) return null

  return (
    <Box gap="s24">
      <DropdownSearchBar
        name={project?.name ?? ''}
        data={projectWorkers.map(({ id, imputableProject }) => ({
          label: imputableProject?.name ?? '',
          value: id,
        }))}
        label={i18n.t('expenses_v2.forms.fields.projects.project')}
        ariaLabel={'projects-popover'}
        labelNotFound={i18n.t('common.projects_not_found')}
        searchLabel={i18n.t('common.search')}
        sortLabel={i18n.t('common.name')}
        placeholder={i18n.t('expenses_v2.forms.fields.projects.placeholder')}
        selectLabel={i18n.t('expenses_v2.forms.fields.projects.placeholder')}
        disabled={readonly}
        autofocus={false}
        onSelectEmpty={() => onChange(null)}
        onSelect={({ value: projectWorkerId }) =>
          onChange({ projectWorkerId: projectWorkerId as string, subprojectId: null })
        }
        mandatory={mandatory}
        loading={loading}
        error={showErrorMessage(project?.name)}
      />

      {subprojects.length > 0 && (
        <DropdownSearchBar
          name={subproject?.name ?? ''}
          data={subprojects.map(({ id, name }) => ({ label: name, value: id ?? '' }))}
          label={i18n.t('expenses_v2.forms.fields.projects.subproject')}
          ariaLabel={'subprojects-popover'}
          labelNotFound={i18n.t('common.projects_not_found')}
          searchLabel={i18n.t('common.search')}
          sortLabel={i18n.t('common.name')}
          placeholder={i18n.t('expenses_v2.forms.fields.projects.placeholder')}
          selectLabel={i18n.t('expenses_v2.forms.fields.projects.placeholder')}
          disabled={readonly}
          autofocus={false}
          onSelectEmpty={() => onChange({ ...value, subprojectId: null })}
          onSelect={({ value: subprojectId }) =>
            onChange({ ...value, subprojectId: subprojectId as string })
          }
          mandatory={mandatory}
          loading={loading}
          error={showErrorMessage(subproject?.name)}
        />
      )}
    </Box>
  )
}

export default observer(ProjectFields)
